import { useContext, useMemo } from "react";

import { useNavigate } from "react-router-dom";

import { Context as AuthContext, State as AuthState } from "../context/Auth";

import useLogout from "../hooks/useLogout";

import {
    APIRequestError,
    APIErrorType,
    APIRequestInit,
    fetchAPI,
} from "./util";

export interface Resource {
    object: "upload" | "transaction";

    id: string;
    metadata?: Record<string, any>;

    createdAt: Date;
    updatedAt: Date;
}

export class Service {
    navigate: ReturnType<typeof useNavigate>;
    logout: () => void;
    authState?: AuthState;
    redirectRoute: string;

    constructor(
        navigate: Service["navigate"],
        logout: () => void,
        redirectRoute: string,
        authState?: AuthState,
    ) {
        this.navigate = navigate;
        this.logout = logout;
        this.authState = authState;
        this.redirectRoute = redirectRoute;
    }

    // TODO Make generic 'fetch' wrapper here which handles errors as well
    async internalFetchAPI(path: string, init?: APIRequestInit): Promise<any> {
        if (this.authState && this.authState.token) {
            init = {
                ...init,
                headers: {
                    ...init?.headers,
                    Authorization: `Bearer ${this.authState.token}`,
                },
            };
        }

        try {
            return await fetchAPI(path, init);
        } catch (err: any) {
            if (err instanceof APIRequestError) {
                // Only redirect if user was previously authenticated but token is now invalid
                // Don't redirect if user was never authenticated (check for user/token existence)
                if (
                    this.authState?.user &&
                    (err.type === APIErrorType.TOKEN_EXPIRED ||
                        err.type === APIErrorType.AUTHENTICATION_MISSING ||
                        err.type === APIErrorType.INVALID_TOKEN)
                ) {
                    this.logout();
                    this.navigate(this.redirectRoute);
                }
            }

            throw err;
        }
    }

    async fetchAPI<T>(path: string, init?: APIRequestInit): Promise<T> {
        return this.internalFetchAPI(path, init);
    }
}

export const useService = () => {
    const navigate = useNavigate();

    const logout = useLogout();
    const { state } = useContext(AuthContext);

    return useMemo(
        () => new Service(navigate, logout, "/login", state),
        [navigate, state, logout],
    );
};
